import { Avatar, Button, Card, CardContent } from '@bs/ui-components'
import { ArrowLeft, Bookmark, Clock3, Flag, MapPin, MoreVertical, Share2, Undo2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

import { DialogField, EditDialog } from '@/components/ui/edit-dialog'
import { ProfileAside } from '@/features/profile/components/profile-aside'
import { companyLogoUrl } from '@/services/assets'
import { sanitizeJobDescription } from '@/utils/sanitize-html'
import { formatLocation, getCities, getCompanies, getIndustries, getLookup, type CityItem, type CompanyItem, type MetadataItem } from '@/services/metadata'
import {
  applyToJob,
  createJobReport,
  getApplications,
  getJobBySlug,
  getJobReports,
  getSavedJobs,
  saveJob,
  unsaveJob,
  withdrawApplication,
  type Application,
  type JobSearchDetail,
  type SavedJob,
} from '@/features/jobs/api'

export function JobDetailPage() {
  const { jobSlug } = useParams()
  // Whoever linked in (jobs-page.tsx, job-search-page.tsx) passes where "Back to jobs" should
  // actually return to — their own tab/search state, not a bare `/jobs` that drops it. A job URL
  // opened directly (shared link, refresh) carries no such state, hence the `/jobs` fallback.
  const location = useLocation()
  const backTo = (location.state as { from?: string } | null)?.from ?? '/jobs'
  const [shareMessage, setShareMessage] = useState('')
  const [dialog, setDialog] = useState<'share' | 'report' | 'withdraw' | null>(null)

  const [realJob, setRealJob] = useState<JobSearchDetail | null>(null)
  const [notFoundReal, setNotFoundReal] = useState(false)
  const [company, setCompany] = useState<CompanyItem | null>(null)
  const [city, setCity] = useState<CityItem | null>(null)
  const [workspaceTypes, setWorkspaceTypes] = useState<MetadataItem[]>([])
  const [positionTypes, setPositionTypes] = useState<MetadataItem[]>([])
  const [seniorityLevels, setSeniorityLevels] = useState<MetadataItem[]>([])
  const [industries, setIndustries] = useState<MetadataItem[]>([])
  const [savedRow, setSavedRow] = useState<SavedJob | null>(null)
  const [application, setApplication] = useState<Application | null>(null)
  const [reportedJobIds, setReportedJobIds] = useState<Set<string>>(new Set())
  const [reportReasons, setReportReasons] = useState<MetadataItem[]>([])
  const [realReportReason, setRealReportReason] = useState<number | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!jobSlug) return
    let cancelled = false
    getJobBySlug(jobSlug)
      .then(async (job) => {
        if (cancelled) return
        const [companies, cities, workspaceType, positionType, seniorityLevel, industry, reportReason, savedRes, appsRes, reportsRes] = await Promise.all([
          getCompanies({ ids: [job.companyId] }).catch(() => []),
          job.location?.cityId !== undefined ? getCities({ ids: [job.location.cityId] }).catch(() => []) : Promise.resolve([]),
          getLookup('workspaceTypes').catch(() => []),
          getLookup('positionTypes').catch(() => []),
          getLookup('seniorityLevels').catch(() => []),
          getIndustries().catch(() => []),
          getLookup('reportReason').catch(() => []),
          getSavedJobs().catch(() => ({ items: [] as SavedJob[] })),
          getApplications().catch(() => ({ items: [] as Application[] })),
          getJobReports().catch(() => ({ items: [] as { jobId: string }[] })),
        ])
        if (cancelled) return
        setRealJob(job)
        setCompany(companies[0] ?? null)
        setCity(cities[0] ?? null)
        setWorkspaceTypes(workspaceType)
        setPositionTypes(positionType)
        setSeniorityLevels(seniorityLevel)
        setIndustries(industry)
        setReportReasons(reportReason)
        setRealReportReason(reportReason[0]?.elId ?? null)
        setSavedRow(savedRes.items.find((s) => s.jobId === job._id) ?? null)
        setApplication(appsRes.items.find((a) => a.jobId === job._id && a.status !== 'withdrawn') ?? null)
        setReportedJobIds(new Set(reportsRes.items.map((r) => r.jobId)))
      })
      .catch(() => setNotFoundReal(true))
    return () => {
      cancelled = true
    }
  }, [jobSlug])

  useEffect(() => {
    if (!menuOpen) return
    function closeOnOutsideClick(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [menuOpen])

  function snapshot(job: JobSearchDetail) {
    return {
      jobId: job._id,
      companyId: job.companyId,
      companyData: { ...(company?.name && { name: company.name }), ...(company?.logoUrl && { logoUrl: company.logoUrl }) },
      jobData: {
        title: job.title,
        slug: job.slug,
        ...(job.location?.cityId !== undefined && { cityId: job.location.cityId }),
        ...(job.location?.country && { country: job.location.country }),
      },
    }
  }

  async function handleRealApply() {
    if (!realJob) return
    const { application: created } = await applyToJob(snapshot(realJob)).catch(() => ({ application: undefined }))
    if (created) setApplication(created)
  }

  async function handleRealWithdraw() {
    if (!application) return
    await withdrawApplication(application._id).catch(() => {})
    setApplication(null)
  }

  async function handleRealSave() {
    if (!realJob) return
    if (savedRow) {
      await unsaveJob(realJob._id).catch(() => {})
      setSavedRow(null)
      return
    }
    const { savedJob } = await saveJob(snapshot(realJob)).catch(() => ({ savedJob: undefined }))
    if (savedJob) setSavedRow(savedJob)
  }

  /** Copy-link only — there is no real "share by email" delivery path yet (would need a new send
   * pipeline: schema, contract, and delivery wiring, not just this page), so this doesn't pretend
   * to offer one. */
  async function handleRealShare() {
    // Clipboard access can be denied for reasons that have nothing to do with THIS click being
    // genuine (embedded contexts, browser permission policy, older Safari) — silently doing
    // nothing on that failure reads as a dead button. Caught, not left to reject unhandled.
    try {
      await navigator.clipboard.writeText(window.location.href)
      setShareMessage('Job link copied')
    } catch {
      setShareMessage("Couldn't copy — copy the address bar link instead")
    }
    window.setTimeout(() => setShareMessage(''), 2600)
  }

  async function handleRealReport() {
    if (!realJob || realReportReason === null) return
    const { report } = await createJobReport({ jobId: realJob._id, reason: realReportReason }).catch(() => ({ report: undefined }))
    if (report) setReportedJobIds((current) => new Set(current).add(realJob._id))
    setDialog(null)
  }

  if (realJob) {
    const locationText = formatLocation(city, realJob.location?.country)
    const workMode = workspaceTypes.find((w) => w.elId === realJob.workspaceType)?.value ?? ''
    const seniorityLabel = seniorityLevels.find((s) => realJob.seniorityLevel?.includes(s.elId))?.value
    const positionTypeLabel = positionTypes.find((p) => realJob.positionType?.includes(p.elId))?.value
    const industryLabel = industries.find((i) => realJob.industries?.includes(i.elId))?.value
    const alreadyReported = reportedJobIds.has(realJob._id)
    const logoSrc = companyLogoUrl(company?.logoUrl)
    return (
      <main className="mx-auto max-w-[1460px] px-4 py-8 sm:px-6 md:py-12 lg:px-8">
        <Link to={backTo} className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-accent">
          <ArrowLeft className="size-4" /> Back to jobs
        </Link>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1">
            <Card className="overflow-hidden rounded-[20px]">
              <div className="border-b border-border bg-gradient-to-br from-[#f5f7ff] to-white p-6 sm:p-9">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                  <Avatar {...(logoSrc ? { src: logoSrc } : {})} name={company?.name ?? realJob.title} className="size-20 shrink-0 text-xl" />
                  <div className="min-w-0 flex-1">
                    <h1 className="mt-3 text-3xl font-semibold tracking-tight text-primary">{realJob.title}</h1>
                    {company?.slug ? (
                      <Link to={`/company/${company.slug}`} className="mt-2 inline-flex text-lg font-medium text-[#404040] hover:text-accent hover:underline">
                        {company.name}
                      </Link>
                    ) : (
                      <p className="mt-2 text-lg font-medium text-[#404040]">{company?.name ?? ''}</p>
                    )}
                    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                      {locationText ? <span className="flex items-center gap-1.5"><MapPin className="size-4" /> {locationText}</span> : null}
                      {workMode ? <span>{workMode}</span> : null}
                      <span className="flex items-center gap-1.5"><Clock3 className="size-4" /> {new Date(realJob.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
              </div>
              <CardContent className="p-6 sm:p-9">
                {realJob.description ? (
                  <section>
                    <h2 className="text-xl font-semibold text-primary">About the role</h2>
                    <div className="mt-3 max-w-3xl leading-7 text-[#555]" dangerouslySetInnerHTML={{ __html: sanitizeJobDescription(realJob.description) }} />
                  </section>
                ) : null}
                <div className="mt-9 flex flex-wrap items-center gap-3 border-t border-border pt-6">
                  {application ? (
                    <Button size="lg" variant="outline" onClick={() => void handleRealWithdraw()}><Undo2 className="size-4" /> Withdraw application</Button>
                  ) : (
                    <Button size="lg" onClick={() => void handleRealApply()}>Apply for this job</Button>
                  )}
                  <Button size="lg" variant="outline" onClick={() => void handleRealSave()} disabled={!!application}><Bookmark className="size-4" /> {savedRow ? 'Remove saved' : 'Save'}</Button>
                  {realJob.salary?.amount ? <span className="ml-auto text-sm font-semibold text-primary">{realJob.salary.amount}</span> : null}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="w-full shrink-0 lg:w-[280px]">
            <Card className="rounded-[20px]">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <span className="text-sm font-semibold text-accent">
                    {application ? 'Applied' : savedRow ? 'Saved' : ''}
                  </span>
                  <div ref={menuRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setMenuOpen((value) => !value)}
                      aria-label="More actions"
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                      className="rounded-full p-1.5 text-muted-foreground transition hover:bg-muted hover:text-primary"
                    >
                      <MoreVertical className="size-5" />
                    </button>
                    {menuOpen ? (
                      <div role="menu" className="absolute right-0 top-[calc(100%+8px)] z-10 w-44 overflow-hidden rounded-xl border border-border bg-white p-1.5 shadow-[0_14px_40px_rgba(0,7,79,0.16)]">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setMenuOpen(false); void handleRealShare() }}
                          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-[#404040] transition hover:bg-muted hover:text-primary"
                        >
                          <Share2 className="size-4" /> Share
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          disabled={alreadyReported}
                          onClick={() => { setMenuOpen(false); setDialog('report') }}
                          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-[#404040] transition hover:bg-muted hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Flag className="size-4" /> {alreadyReported ? 'Reported' : 'Report'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
                {shareMessage ? <p className="mt-2 text-xs font-medium text-[#00895b]">{shareMessage}</p> : null}
                {seniorityLabel || positionTypeLabel || industryLabel ? (
                  <div className="mt-5 grid gap-4 border-t border-border pt-5 text-sm">
                    {seniorityLabel ? <div><p className="font-semibold text-primary">Seniority</p><p className="mt-1 text-[#555]">{seniorityLabel}</p></div> : null}
                    {positionTypeLabel ? <div><p className="font-semibold text-primary">Type</p><p className="mt-1 text-[#555]">{positionTypeLabel}</p></div> : null}
                    {industryLabel ? <div><p className="font-semibold text-primary">Industry</p><p className="mt-1 text-[#555]">{industryLabel}</p></div> : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>

          <ProfileAside />
        </div>
        {dialog === 'report' ? (
          <EditDialog title="Report Job" submitLabel="Report job" description="We'll review this report — it's sent for real." onClose={() => setDialog(null)} onSubmit={() => void handleRealReport()}>
            <DialogField label="Why are you reporting this job?">
              <select
                value={realReportReason ?? ''}
                onChange={(event) => setRealReportReason(Number(event.target.value))}
                className="min-h-11 rounded-xl border border-input-border bg-input px-4 text-base outline-none focus:border-accent"
              >
                {reportReasons.map((option) => <option key={option.elId} value={option.elId}>{option.value}</option>)}
              </select>
            </DialogField>
          </EditDialog>
        ) : null}
      </main>
    )
  }

  // Real fetch still pending — avoid a flash of "Job not found" while it's in flight.
  if (jobSlug && !notFoundReal) return null

  /**
   * The real fetch resolved to nothing. This used to fall through to `getJob(jobSlug)` against the
   * mock provider and render a FIXTURE JOB under the not-found slug — a page a candidate could
   * read, share, and apply to, for a job that does not exist.
   */
  return (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold text-primary">Job not found</h1>
      <p className="mt-3 text-muted-foreground">This job may have been closed or removed.</p>
      <Button className="mt-6" asChild><Link to="/jobs">Back to jobs</Link></Button>
    </main>
  )
}
